// Read-side hook for the PersonalBriefPane on /terminal. Hits the
// composite GET /v1/me/brief endpoint and exposes per-strip emptiness so
// each strip can independently fall back to teaching copy.
//
// Phase 2: only `on_your_radar` returns live data. Phases 3-4 will light
// up the other two strips without any change to this hook's shape.
//
// Refetch policy: 60s stale time matches the brief's "ambient awareness"
// nature — not a real-time ticker, just a fresh-on-load summary. The SSE
// hook (Phase 3) will push deltas in between refetches.

import { apiFetch } from '@/lib/api';
import { useAuth } from '@/state/auth';
import { useQuery } from '@tanstack/react-query';

export interface OnYourRadarRow {
  pipeline_item_id: string;
  entity_type: string;
  entity_id: string;
  title: string | null;
  agency_raw_name: string | null;
  jurisdiction: string | null;
  closing_date: string | null;
  estimated_value: number | null;
  probability: number | null;
  priority: string;
}

export interface SinceLastLookedRow {
  entity_type: string;
  entity_id: string;
  /** Display title — tender_id for tenders, canonical_name for agencies,
   *  vendor_name for legal_entities. May be null. */
  entity_title: string | null;
  change_kind: string;
  change_summary: string | null;
  ts: string;
}

interface MarketPulseRow {
  signal_id: string;
  opportunity_id: string;
  score: number;
  reason: string | null;
  signal_type: string | null;
  computed_at: string;
}

interface BriefResponse {
  since_last_looked: SinceLastLookedRow[];
  on_your_radar: OnYourRadarRow[];
  market_pulse: MarketPulseRow[];
  generated_at: string;
}

export interface UseBriefDataResult {
  data: BriefResponse | undefined;
  isLoading: boolean;
  error: unknown;
  isEmpty: { sll: boolean; oyr: boolean; mp: boolean };
}

export function useBriefData(): UseBriefDataResult {
  const accessToken = useAuth((s) => s.accessToken);
  const q = useQuery({
    enabled: !!accessToken,
    queryKey: ['brief'],
    queryFn: () => apiFetch<BriefResponse>('/v1/me/brief'),
    staleTime: 60_000,
  });

  return {
    data: q.data,
    isLoading: q.isLoading,
    error: q.error,
    isEmpty: {
      sll: !q.data || q.data.since_last_looked.length === 0,
      oyr: !q.data || q.data.on_your_radar.length === 0,
      mp: !q.data || q.data.market_pulse.length === 0,
    },
  };
}
